import NetInfo from '@react-native-community/netinfo';
import {createAsyncStoragePersister} from '@tanstack/query-async-storage-persister';
import {
  QueryClient,
  onlineManager,
  focusManager,
  Query,
} from '@tanstack/react-query';
import {
  persistQueryClientRestore,
  persistQueryClientSave,
} from '@tanstack/react-query-persist-client';
import {AppState} from 'react-native';

import {storage} from '@/services/storage';
import {CACHE_EXCLUDED_KEYS} from '@/types/queryKeys';
import {isLocalDev, isWeb} from '@/utils/platform';

const CACHE_VERSION = 21;
const CACHE_STORAGE_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const getStorageKey = (version: number) => `${CACHE_STORAGE_KEY}_v${version}`;

const CACHE_TIME = Infinity;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: !isWeb || !isLocalDev,
    },
  },
});

const persister = createAsyncStoragePersister({
  key: getStorageKey(CACHE_VERSION),
  storage,
  throttleTime: 3000,
  // @ts-ignore - on web we use indexedDB for storage. It doesn't require serialization so it's ok to return pure data
  serialize: data => {
    return isWeb ? data : JSON.stringify(data);
  },
  deserialize: cachedData => {
    const parsedCached = isWeb ? cachedData : JSON.parse(cachedData);
    return {
      ...parsedCached,
      clientState: {
        ...parsedCached.clientState,
        queries: parsedCached.clientState.queries.filter(
          // @ts-ignore
          (query: Query) => !CACHE_EXCLUDED_KEYS.includes(query.queryKey[0]),
        ),
      },
    };
  },
});

const clearDeprecatedCache = async () => {
  try {
    // remove legacy cache
    if (CACHE_VERSION > 1) {
      await Promise.all(
        Array.from({length: CACHE_VERSION - 1}).map((_, i) =>
          storage.removeItem(getStorageKey(i + 1)),
        ),
      );
    }
  } catch (error) {
    //ignore error
  }
};

export const setupReactQuery = async (): Promise<void> => {
  if (!isWeb) {
    onlineManager.setEventListener(setOnline => {
      return NetInfo.addEventListener(state => {
        setOnline(!!state.isConnected);
      });
    });

    focusManager.setEventListener(handleFocus => {
      const subscription = AppState.addEventListener('change', state => {
        if (state === 'active') {
          handleFocus();
        }
      });

      return () => {
        subscription.remove();
      };
    });
  }

  clearDeprecatedCache();

  await persistQueryClientRestore({
    queryClient,
    persister,
    maxAge: CACHE_TIME,
  });

  queryClient.getQueryCache().subscribe(event => {
    // it's save to ignore other events - confirmed with library maintainer
    if (['added', 'removed', 'updated'].includes(event.type)) {
      persistQueryClientSave({queryClient, persister});
    }
  });
};
